import { useState } from 'react';
import axios from 'axios';
import { FileDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function MonthlyReport() {
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [month, setMonth] = useState((new Date().getMonth() + 1).toString());
  const [generating, setGenerating] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from({length: 5}, (_, i) => (currentYear - 2 + i).toString());

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const response = await axios.get(`${API}/export/monthly-report-pdf?year=${year}&month=${month}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Monthly_Report_${year}-${month.padStart(2, '0')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Monthly report downloaded');
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) {
        toast.error('Admin access required');
      } else if (err.response?.data instanceof Blob) {
        // When responseType is blob, error responses come as blobs too
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          toast.error(parsed.detail || 'Failed to generate report');
        } catch {
          toast.error('Failed to generate report. Please try again.');
        }
      } else {
        toast.error(err.response?.data?.detail || 'Failed to generate report. Please try again.');
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="monthly-report-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="monthly-report-title">MONTHLY REPORT</h1>
        <p className="text-sm text-text-muted mt-2">Generate an AI-powered monthly production summary with schedule analysis, stage bottlenecks, vendor performance, and stock movement.</p>
      </div>

      <div className="bg-white border border-border rounded-sm p-8 max-w-lg">
        <h2 className="text-sm font-bold uppercase tracking-wider text-gray-800 mb-6">Generate Report</h2>
        <div className="flex items-end gap-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Year</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-28 mt-1.5 rounded-none" data-testid="report-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Month</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-40 mt-1.5 rounded-none" data-testid="report-month">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => <SelectItem key={i+1} value={(i+1).toString()}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={handleGenerate}
            disabled={generating}
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium min-w-[180px]"
            data-testid="generate-report-btn"
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />GENERATING...</>
            ) : (
              <><FileDown className="w-4 h-4 mr-2" />GENERATE PDF</>
            )}
          </Button>
        </div>
        {generating && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-sm">
            <p className="text-xs text-blue-700">Analyzing production data and generating AI summary. This may take 15-30 seconds...</p>
          </div>
        )}
      </div>

      <div className="bg-white border border-border rounded-sm p-6">
        <h2 className="text-sm font-bold uppercase tracking-wider text-gray-800 mb-3">Report Contents</h2>
        <div className="grid grid-cols-2 gap-3 text-xs text-gray-600">
          <div className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0"></span>
            <span><strong>Executive Summary</strong> — AI-generated overview of the month's production performance</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0"></span>
            <span><strong>Schedule Performance</strong> — Detailed schedule vs dispatch fulfillment rates</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 shrink-0"></span>
            <span><strong>Stage Bottlenecks</strong> — Parts stuck in stages with duration analysis</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-1.5 shrink-0"></span>
            <span><strong>Vendor Performance</strong> — Turnaround times per vendor with part breakdown</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0"></span>
            <span><strong>Stock Movement</strong> — Opening vs closing stock comparison across stages</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-1.5 shrink-0"></span>
            <span><strong>Recommendations</strong> — AI-powered actionable suggestions for improvement</span>
          </div>
        </div>
      </div>
    </div>
  );
}
